import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { useLocations } from "@/context/LocationContext";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/ui-bits";
import DateScopePicker, { scopeToParams, downloadCsv } from "@/components/DateScopePicker";

const STAGES = ["intake", "production", "packing", "dispatch"];

export default function Waste() {
  const { params, selected } = useLocations();
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [batches, setBatches] = useState([]);
  const [packing, setPacking] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ stage: "production", material_id: "", batch_id: "", packing_id: "", quantity: 0, unit: "units", reason: "", notes: "" });
  const [scope, setScope] = useState({ mode: "today" });

  const load = async () => {
    const [w, m, b, p] = await Promise.all([
      api.get("/waste", { params: { ...params(), ...scopeToParams(scope) } }),
      api.get("/materials"),
      api.get("/batches", { params: params() }),
      api.get("/packing", { params: params() }),
    ]);
    setRows(w.data); setMaterials(m.data); setBatches(b.data); setPacking(p.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, scope]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        quantity: Number(form.quantity),
        material_id: form.material_id || null,
        batch_id: form.batch_id || null,
        packing_id: form.packing_id || null,
        location_id: selected || null,
      };
      await api.post("/waste", body);
      toast.success("Waste recorded");
      setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete waste record?")) return;
    await api.delete(`/waste/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader title="Waste" eyebrow="Production · Loss tracking">
        <Button className="h-12 px-5 gap-2" onClick={()=>setOpen(true)} data-testid="new-waste-button">
          <Plus className="h-5 w-5" /> Record waste
        </Button>
      </PageHeader>

      <Panel
        title={`Waste records (${rows.length})`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <DateScopePicker value={scope} onChange={setScope} />
            {scope.mode === "history" && rows.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadCsv(
                    `waste-${scope.from_date}-to-${scope.to_date}.csv`,
                    rows,
                    [
                      { header: "Stage", value: "stage" },
                      { header: "Batch code", value: "batch_code" },
                      { header: "Label code", value: "label_code" },
                      {
                        header: "Product",
                        value: (w) =>
                          materials.find((m) => m.id === w.material_id)?.name ||
                          batches.find((b) => b.id === w.batch_id)?.recipe_name ||
                          packing.find((p) => p.id === w.packing_id)?.recipe_name ||
                          w.material_name ||
                          "",
                      },
                      { header: "Quantity", value: "quantity" },
                      { header: "Unit", value: "unit" },
                      { header: "Reason", value: "reason" },
                      { header: "Notes", value: "notes" },
                      { header: "Recorded at", value: "created_at" },
                      { header: "Recorded by", value: "recorded_by_name" },
                    ]
                  )
                }
                data-testid="waste-export-csv"
              >
                Export CSV
              </Button>
            )}
          </div>
        }
      >
        {rows.length === 0 ? (
          <div className="py-10 text-center text-stone-500">No waste recorded.</div>
        ) : (
          <table className="w-full" data-testid="waste-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="py-3">Stage</th>
                <th>Reason</th>
                <th>Product</th>
                <th>Batch / Label</th>
                <th className="text-right">Qty</th>
                <th>Recorded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => {
                const mat = materials.find((m) => m.id === w.material_id);
                const bat = batches.find((b) => b.id === w.batch_id);
                const pack = packing.find((p) => p.id === w.packing_id);
                // Product name — the operator's north star for reading
                // the waste log. Raw materials show the ingredient name;
                // downstream stages show the recipe/product name.
                const productName =
                  mat?.name ||
                  bat?.recipe_name ||
                  pack?.recipe_name ||
                  w.material_name ||
                  "—";
                const codeCell = bat?.batch_code || pack?.label_code || "—";
                return (
                  <tr
                    key={w.id}
                    className="border-t border-border h-16"
                    data-testid={`waste-row-${w.id}`}
                  >
                    <td>
                      <span className="pill-stage bg-stone-100 text-stone-700">
                        {w.stage}
                      </span>
                    </td>
                    <td>{w.reason}</td>
                    <td
                      className="font-medium"
                      data-testid={`waste-product-${w.id}`}
                    >
                      {productName}
                    </td>
                    <td className="font-mono text-xs text-stone-600">
                      {codeCell}
                    </td>
                    <td className="text-right tnum">
                      {fmt2(w.quantity)} {w.unit}
                    </td>
                    <td className="text-stone-600 text-sm">
                      {w.created_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(w.id)}
                        data-testid={`delete-waste-${w.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="waste-dialog">
          <DialogHeader><DialogTitle className="font-display">Record waste</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Stage</Label>
              <Select
                value={form.stage}
                onValueChange={(v) => {
                  // Auto-switch unit: intake is weight-based (raw material
                  // in kg), everything downstream counts in units/packs.
                  const nextUnit = v === "intake" ? "kg" : "units";
                  setForm({ ...form, stage: v, unit: nextUnit });
                }}
              >
                <SelectTrigger className="h-12" data-testid="waste-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.stage === "intake" && (
              <div>
                <Label>Material</Label>
                <Select value={form.material_id} onValueChange={(v)=>setForm({...form, material_id: v})}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="Pick material" /></SelectTrigger>
                  <SelectContent>{materials.map((m)=><SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.stage === "production" && (
              <div>
                <Label>Production batch</Label>
                <Select value={form.batch_id} onValueChange={(v)=>setForm({...form, batch_id: v})}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="Pick batch" /></SelectTrigger>
                  <SelectContent>{batches.map((b)=><SelectItem key={b.id} value={b.id}>{b.batch_code} · {b.recipe_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.stage === "packing" && (
              <div>
                <Label>Packing record</Label>
                <Select value={form.packing_id} onValueChange={(v)=>setForm({...form, packing_id: v})}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="Pick label" /></SelectTrigger>
                  <SelectContent>{packing.map((p)=><SelectItem key={p.id} value={p.id}>{p.label_code} · {p.recipe_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantity</Label><Input type="number" step="0.01" className="h-12 mt-1" value={form.quantity} onChange={(e)=>setForm({...form, quantity: e.target.value})} required data-testid="waste-qty" /></div>
              <div><Label>Unit</Label><Input className="h-12 mt-1" value={form.unit} onChange={(e)=>setForm({...form, unit: e.target.value})} /></div>
            </div>
            <div><Label>Reason</Label><Input className="h-12 mt-1" value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} required data-testid="waste-reason" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="waste-submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
